import type { JSX } from 'react';
import { Alert, Center, Loader, Stack, Tabs, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { benchmarkApi } from '../api';
import { BenchmarkCatalogBody } from '../modules/costs/BenchmarkCatalog';
import { Din277Catalog } from '../modules/costs/Din277Catalog';
import { DinCatalog } from '../modules/costs/DinCatalog';
import { ImportLog, LibraryManagerBody } from '../modules/tendering/TextLibraries';
import { InboxPanel } from './InboxPanel';
import { LIBRARY_VIEWS, type LibraryView } from './moduleViews';
import { PageScroll } from '../ui/PageScroll';
import { linkTo } from '../ui/links';

/**
 * Verwaltung › Bibliotheken: the office's reference data that is not a
 * project's - cost benchmarks, tender texts, and whatever library a later
 * module brings - each as a tab, looked after in one place.
 */
export function LibrariesPage({ permissions }: { permissions: readonly string[] }): JSX.Element {
  const { tab } = useParams();
  const navigate = useNavigate();
  // The benchmark catalog is licensed: only users it has been granted to see it (A-22).
  const catalog = useQuery({ queryKey: ['benchmarks', 'me'], queryFn: benchmarkApi.me, enabled: permissions.includes('costs.read') });
  const allowed = (v: LibraryView): boolean => permissions.includes(v.permission) && (v.path !== 'kennwerte' || catalog.data?.allowed === true);
  if (catalog.isLoading) return <Center h={200}><Loader /></Center>;
  const views = LIBRARY_VIEWS.filter(allowed);
  if (views.length === 0) return <Alert color="gray">Für keine Bibliothek freigegeben.</Alert>;
  const current = views.find((v) => v.path === tab);
  if (!current) return <Navigate to={`/bibliotheken/${views[0]!.path}`} replace />;
  const isAdmin = permissions.includes('users.manage');
  // Office-wide reference data is changed by the Administration only (ADR 0020).
  const canManage = permissions.includes('library.manage');

  return (
    <PageScroll>
    <Stack gap="sm" pb="xl">
      <Stack gap={0}>
        <Title order={3}>Bibliotheken</Title>
        <Text size="sm" c="dimmed">Nachschlagedaten des Büros für alle Projekte. Übernommenes wird in den Projekten als Kopie geführt.</Text>
      </Stack>
      <Tabs value={current.path} keepMounted={false}>
        <Tabs.List style={{ flexWrap: 'nowrap', overflowX: 'auto' }}>
          {views.map((v) => <Tabs.Tab key={v.path} value={v.path} title={v.description} component="a" {...linkTo(navigate, `/bibliotheken/${v.path}`)}>{v.label}</Tabs.Tab>)}
        </Tabs.List>
        <Tabs.Panel value="kennwerte" pt="sm"><BenchmarkCatalogBody isAdmin={isAdmin} canEdit={canManage} /></Tabs.Panel>
        <Tabs.Panel value="din276" pt="sm"><DinCatalog canManage={canManage} /></Tabs.Panel>
        <Tabs.Panel value="din277" pt="sm"><Din277Catalog canManage={canManage} /></Tabs.Panel>
        <Tabs.Panel value="ausschreibungstexte" pt="sm"><LibraryManagerBody canEdit={canManage} /></Tabs.Panel>
        <Tabs.Panel value="protokoll" pt="sm"><ImportLog /></Tabs.Panel>
        <Tabs.Panel value="eingang" pt="sm"><InboxPanel /></Tabs.Panel>
      </Tabs>
    </Stack>
    </PageScroll>
  );
}
